import { useTranslation } from "react-i18next";
import PageLayout from "@/components/feature/PageLayout";
import { useLang } from "@/lib/useLang";
import { CAREERS } from "@/data/pages";

export default function CareersPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const c = CAREERS[lang];
  const mailto = `mailto:${c.apply.email}?subject=${encodeURIComponent(c.apply.subject)}`;

  const List = ({ title, items }: { title: string; items: string[] }) => (
    <div>
      <h3 className="font-heading font-semibold text-foreground-950 mb-3">{title}</h3>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-foreground-700 leading-relaxed">
            <i className="ri-check-line text-accent-600 mt-0.5"></i>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <PageLayout eyebrow="CAREERS" title={t("careers.title")} desc={c.intro}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-semibold">{c.position.team}</span>
              <span className="px-2.5 py-1 rounded-md bg-background-50 border border-background-200 text-foreground-600 text-xs">{c.position.type}</span>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">{c.position.title}</h2>
            <p className="text-sm text-foreground-600 flex items-center gap-1.5">
              <i className="ri-map-pin-2-line"></i>
              {c.position.location}
            </p>
          </div>

          <List title={t("careers.duties")} items={c.duties} />
          <List title={t("careers.requirements")} items={c.requirements} />
          <List title={t("careers.preferred")} items={c.preferred} />

          <div>
            <h3 className="font-heading font-semibold text-foreground-950 mb-4">{t("careers.benefits")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {c.benefits.map((b) => (
                <div key={b.title} className="flex gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${b.icon} text-lg`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground-950 text-sm">{b.title}</div>
                    <div className="text-xs text-foreground-600 mt-0.5">{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 p-6 rounded-lg bg-foreground-950 text-background-50">
            <h3 className="font-heading font-bold text-lg mb-4">{c.apply.title}</h3>
            <ol className="space-y-2 mb-6">
              {c.apply.steps.map((s, i) => (
                <li key={s} className="flex gap-3 text-sm text-foreground-300 leading-relaxed">
                  <span className="font-heading font-bold text-accent-300">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
            <a
              href={mailto}
              className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              {t("careers.apply")}
            </a>
            <p className="mt-3 text-xs text-foreground-400 break-all">
              {c.apply.email}
              <br />
              {c.apply.subject}
            </p>
          </div>
        </aside>
      </div>
    </PageLayout>
  );
}
